import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  CloudDone as CloudDoneIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';

const AddOnsSection = () => {
  const [addons, setAddons] = useState([]);
  const [userAddons, setUserAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, addon: null, action: null });

  useEffect(() => {
    loadAddOns();
  }, []);

  const loadAddOns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [addonsResponse, statusResponse] = await Promise.all([
        api.getAddOns(),
        api.getAddOnStatus()
      ]);

      if (addonsResponse.success) {
        setAddons(addonsResponse.data.addons || []);
      }

      if (statusResponse.success) {
        setUserAddons(statusResponse.data.addons || []);
      }
    } catch (err) {
      logger.error('Error loading add-ons:', err);
      setError('Failed to load add-ons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const userHasAddon = (addonId) => {
    // Check if user has this addon with active or trial status
    // The backend only returns active addons (including trials), so if it's in the list, it's active
    return userAddons.some(ua => ua.addon_id === addonId && (ua.status === 'active' || ua.status === 'trial'));
  };

  const getUserAddon = (addonId) => {
    // Get the user's addon subscription details
    return userAddons.find(ua => ua.addon_id === addonId);
  };

  const isInTrial = (addonId) => {
    const userAddon = getUserAddon(addonId);
    return userAddon && (userAddon.status === 'trial' || userAddon.is_trial);
  };

  const handleActivate = (addon) => {
    setConfirmDialog({
      open: true,
      addon: addon,
      action: 'activate'
    });
  };

  const handleCancel = (addon) => {
    setConfirmDialog({
      open: true,
      addon: addon,
      action: 'cancel'
    });
  };

  const handleConfirmAction = async () => {
    const { addon, action } = confirmDialog;
    
    try {
      if (action === 'activate') {
        setActivating(true);
        setError(null);
        setSuccess(null);
        
        const result = await api.activateAddOn(addon.id);
        
        if (result.success) {
          setSuccess(`${addon.name} activated successfully!`);
          await loadAddOns();
          setTimeout(() => setSuccess(null), 5000);
        } else {
          setError(result.message || 'Failed to activate add-on');
        }
      } else if (action === 'cancel') {
        setCancelling(true);
        setError(null);
        setSuccess(null);
        
        const result = await api.cancelAddOn(addon.id);
        
        if (result.success) {
          setSuccess(`${addon.name} cancelled successfully. You can continue using it until the end of your billing period.`);
          await loadAddOns();
          setTimeout(() => setSuccess(null), 5000);
        } else {
          setError(result.message || 'Failed to cancel add-on');
        }
      }
      
      setConfirmDialog({ open: false, addon: null, action: null });
    } catch (err) {
      logger.error(`Error ${action === 'activate' ? 'activating' : 'cancelling'} add-on:`, err);
      setError(err.response?.data?.message || `Failed to ${action} add-on`);
    } finally {
      setActivating(false);
      setCancelling(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getPriceWithVAT = (price) => {
    const vatRate = 0.21; // 21% VAT
    return price * (1 + vatRate);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Subscription Add-Ons
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enhance your subscription with powerful integrations. Add-ons are billed monthly and can be cancelled anytime.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {addons.map((addon) => {
          const hasAddon = userHasAddon(addon.id);
          const userAddon = getUserAddon(addon.id);
          const inTrial = isInTrial(addon.id);
          const daysRemaining = userAddon?.days_remaining;
          
          return (
            <Grid item xs={12} md={6} key={addon.id}>
              <Card 
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: hasAddon ? '2px solid' : '1px solid',
                  borderColor: hasAddon ? (inTrial ? 'warning.main' : 'success.main') : 'divider',
                  position: 'relative'
                }}
              >
                {hasAddon && (
                  <Chip
                    icon={inTrial ? <ScheduleIcon /> : <CheckCircleIcon />}
                    label={inTrial ? (daysRemaining !== undefined && daysRemaining !== null && daysRemaining >= 0 ? `Trial (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left)` : 'Trial') : 'Active'}
                    color={inTrial ? 'warning' : 'success'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    {addon.code === 'hubspot' && <CloudDoneIcon sx={{ mr: 1, color: 'primary.main' }} />}
                    {addon.code === 'salesforce' && <CloudDoneIcon sx={{ mr: 1, color: 'info.main' }} />}
                    <Typography variant="h6" component="h3">
                      {addon.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {addon.description}
                  </Typography>

                  {inTrial && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        You're currently in a {userAddon?.trial_days || 7}-day trial period. 
                        {daysRemaining !== undefined && daysRemaining !== null && daysRemaining >= 0 ? (
                          <> {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining.</>
                        ) : (
                          <> Trial ending soon.</>
                        )}
                      </Typography>
                    </Alert>
                  )}

                  <Box mb={2}>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {formatPrice(addon.price_eur)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      per month (excl. VAT)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(getPriceWithVAT(addon.price_eur))} incl. VAT
                    </Typography>
                  </Box>

                  {addon.features && addon.features.length > 0 && (
                    <List dense sx={{ mb: 2 }}>
                      {addon.features.map((feature, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Box mt="auto" pt={2}>
                    {hasAddon ? (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<CancelIcon />}
                        onClick={() => handleCancel(addon)}
                        disabled={cancelling}
                      >
                        Cancel Add-On
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={() => handleActivate(addon)}
                        disabled={activating}
                      >
                        Add to Subscription
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {addons.length === 0 && (
        <Alert severity="info">
          No add-ons available at this time. Please check back later.
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, addon: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'activate' ? 'Activate Add-On' : 'Cancel Add-On'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.addon && (
            <>
              <Typography variant="body1" gutterBottom>
                {confirmDialog.action === 'activate' ? (
                  <>
                    Are you sure you want to add <strong>{confirmDialog.addon.name}</strong> to your subscription?
                    <br /><br />
                    This will add {formatPrice(confirmDialog.addon.price_eur)} per month (excl. VAT) to your subscription.
                  </>
                ) : (
                  <>
                    Are you sure you want to cancel <strong>{confirmDialog.addon.name}</strong>?
                    <br /><br />
                    You will continue to have access until the end of your current billing period.
                  </>
                )}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, addon: null, action: null })}
            disabled={activating || cancelling}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog.action === 'activate' ? 'primary' : 'error'}
            disabled={activating || cancelling}
            startIcon={(activating || cancelling) && <CircularProgress size={16} />}
          >
            {activating ? 'Activating...' : cancelling ? 'Cancelling...' : confirmDialog.action === 'activate' ? 'Activate' : 'Cancel Add-On'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddOnsSection;

